import { lumi } from '@/lib/lumi'

export type EnrollmentStatus = 'pending' | 'approved' | 'rejected'

export interface CourseEnrollment {
  _id: string
  userId: string
  courseId: string
  courseSlug: string
  courseTitle: string
  fullName: string
  email: string
  phone?: string
  note?: string
  status: EnrollmentStatus
  documentsAccess: boolean
  recognitionUrl?: string
  recognitionName?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

function nowIso() {
  return new Date().toISOString()
}

export function statusLabel(status: EnrollmentStatus | undefined) {
  if (status === 'approved') return 'Aceptado en el curso'
  if (status === 'rejected') return 'Solicitud rechazada'
  return 'Pendiente de revisión'
}

export async function fetchMyEnrollment(userId: string, courseId: string) {
  const { list } = await lumi.entities.enrollments.list({
    filter: { userId, courseId },
    sort: { createdAt: -1 },
    limit: 1,
  })
  return (list?.[0] as CourseEnrollment | undefined) ?? undefined
}

export async function fetchMyEnrollments(userId: string) {
  const { list } = await lumi.entities.enrollments.list({
    filter: { userId },
    sort: { updatedAt: -1 },
  })
  return (list ?? []) as CourseEnrollment[]
}

export async function fetchAllEnrollments() {
  const { list } = await lumi.entities.enrollments.list({
    sort: { createdAt: -1 },
    limit: 200,
  })
  return (list ?? []) as CourseEnrollment[]
}

export async function createEnrollmentRequest(payload: {
  userId: string
  courseId: string
  courseSlug: string
  courseTitle: string
  fullName: string
  email: string
  phone: string
  note: string
}) {
  const existing = await fetchMyEnrollment(payload.userId, payload.courseId)
  if (existing) return existing

  const stamp = nowIso()
  return (await lumi.entities.enrollments.create({
    userId: payload.userId,
    courseId: payload.courseId,
    courseSlug: payload.courseSlug,
    courseTitle: payload.courseTitle,
    fullName: payload.fullName,
    email: payload.email,
    phone: payload.phone,
    note: payload.note,
    status: 'pending',
    documentsAccess: false,
    creator: payload.userId,
    createdAt: stamp,
    updatedAt: stamp,
  })) as CourseEnrollment
}

export async function updateEnrollment(
  id: string,
  updates: Partial<Pick<CourseEnrollment, 'status' | 'documentsAccess' | 'recognitionUrl' | 'recognitionName'>>,
) {
  if (typeof id !== 'string' || id === '[object Object]') {
    throw new Error('ID inválido')
  }
  return (await lumi.entities.enrollments.update(id, {
    ...updates,
    updatedAt: nowIso(),
  })) as CourseEnrollment
}

export async function uploadRecognitionFile(file: File) {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  const isImage = file.type === 'image/png' || file.type === 'image/jpeg' || /\.(png|jpe?g)$/i.test(file.name)
  if (!isPdf && !isImage) {
    throw new Error('Solo se aceptan archivos PDF o imágenes (PNG, JPG).')
  }
  const results = await lumi.tools.file.upload([file])
  const first = results?.[0]
  if (!first?.fileUrl) {
    throw new Error(first?.uploadError || 'No se pudo subir el reconocimiento.')
  }
  return { url: first.fileUrl, name: first.fileName || file.name }
}
